import { useState, useRef, useEffect, useCallback, memo, useMemo } from "react"
import CustomDate from "../utils/CustomDate"
import EventPopup from "./EventPopup"
import IntervalTimeline from "./IntervalTimeline"
import { scrollPositionToDate, dateToScrollPosition } from "../utils/datePositionUtils"
import { STARTING_GAP, LONG_MARKER_HEIGHT } from "./renderTimeline"

const ScrollCounter = ({
	events,
	onDateChange,
	timelineBackground,
	onEventSelect,
	selectedEvent,
	scrollPosition,
	setScrollPosition,
	interval,
	categories,
	stylings,
	handleMarkerCountUpdate,
	timelineData,
	onTimelineUpdate,
	scrollPositionY,
	setScrollPositionY,
	isAuthenticated,
	TIMELINE_HEIGHT,
	onCategoryFilter,

}) => {
	const [isDragging, setIsDragging] = useState(false)
	const [startX, setStartX] = useState(0)
	const [startY, setStartY] = useState(0);
	const containerRef = useRef(null)
	const [maxeventVerticalHeight, setmaxeventVerticalHeight] = useState(0)
	// Responsive width
	const [visibleWidth, setVisibleWidth] = useState(window.innerWidth) // no padding

	// Distance between multiple event lines
	const distance_between_event_lines = 10

	const prevDateRef = useRef(null);
	const prevIntervalRef = useRef(null);

	const isFiltered = window.timelineEvents && window.timelineEvents.length !== events.length;

	// State for scrollbar dragging
	const [isScrollbarDragging, setIsScrollbarDragging] = useState(false);
	const [scrollbarStartY, setScrollbarStartY] = useState(0);

	useEffect(() => {
		if (!events || events.length === 0) {
			setmaxeventVerticalHeight(0); // Reset if no events
			return;
		}

		// First determine which events are actually visible in the current viewport
		const visibleStartDate = scrollPositionToDate(scrollPosition - visibleWidth / 2, interval);
		const visibleEndDate = scrollPositionToDate(scrollPosition + visibleWidth / 2, interval);

		let max_value_height = 0;
		let max_value_altitude = 0;
		let maxHeight = 0;

		events.forEach(event => {
			// Skip events that aren't in the current date range or are filtered out
			if (event.shouldRender === false) return;

			// Check if event is within the current visible date range
			// const isInCurrentView = event.startDate && 
			// 	event.startDate.getTime() >= visibleStartDate.getTime() && 
			// 	event.startDate.getTime() <= visibleEndDate.getTime();

			// if (!isInCurrentView) return;
			// NOTE: Scrollbar sadece şuanda ekranda olanlar için çalışsın istersek commentleri kaldırmamız yeterli

			// Now calculate height only for visible events
			if (typeof event.altitude === 'number' && typeof event.height === 'number') {
				const eventTop = event.altitude + event.height;
				if (eventTop > maxHeight) {
					maxHeight = eventTop;
					max_value_altitude = event.altitude
					max_value_height = event.height
				}
			}

		});
		// console.log(max_value_height)

		// Add a small buffer to max height to ensure content doesn't touch the edge
		setmaxeventVerticalHeight(maxHeight > 0 ? maxHeight + STARTING_GAP + max_value_height * 2 : 0);

	}, [events, interval, scrollPosition, visibleWidth]) // Added dependencies for viewport calculation


	const isDateVisible = useCallback((date) => {
		if (!date) return false;
		const dateObj = date instanceof CustomDate ? date : new CustomDate(
			date.year, date.month || 0, date.day || 1
		);
		const leftDate = scrollPositionToDate(scrollPosition - visibleWidth / 2, interval);
		const rightDate = scrollPositionToDate(scrollPosition + visibleWidth / 2, interval);

		return dateObj.isBetween(leftDate.addIntervals(-2, interval.value), rightDate.addIntervals(2, interval.value));

	}, [scrollPosition, interval, visibleWidth]);

	// Render event duration lines using event.altitude (precomputed)
	const renderEventLines = useCallback(() => {
		if (!events || events.length === 0) return null;

		// Count events with altitude values to help debugging
		const eventsWithAltitude = events.filter(e => typeof e.altitude === 'number').length;
		// console.log(`Lines: ${eventsWithAltitude} of ${events.length} events have altitude values`);

		return events.map(event => {
			// Skip events that are filtered out (shouldRender === false)
			if (event.shouldRender === false) return null;

			if (!event.lines || event.lines.length === 0) return null;

			// Calculate base altitude considering scroll position Y
			const baseAltitude = (event.altitude) + 8 + scrollPositionY;
			// Ensure a minimum altitude to avoid edge lines overlap
			const minAltitude = STARTING_GAP + scrollPositionY;

			return event.lines.map((line, lineIndex) => {
				// Calculate vertical position with a minimum to prevent overlap with timeline
				const calculatedPosition = baseAltitude + (lineIndex * distance_between_event_lines);
				const verticalPosition = Math.max(calculatedPosition, minAltitude);

				// Skip rendering if the line is below the visible area base
				if (verticalPosition < 0) return null;
				const lineHeight = line.type === 'thick' ? 20 : 4;
				const startDate = event.startDate;
				const endDate = event.endDate;

				// Add null checks before calling dateToScrollPosition
				if (!startDate) return null; // Skip rendering if no startDate

				const isStartVisible = isDateVisible(scrollPositionToDate(dateToScrollPosition(startDate, interval), interval));
				const isEndVisible = endDate ? isDateVisible(scrollPositionToDate(dateToScrollPosition(endDate, interval), interval)) : false;
				const rounded_down_scroll_position = (Math.floor(scrollPosition / interval.pixelWidth) * interval.pixelWidth)

				let lineStyle = {};

				if (isStartVisible && isEndVisible && endDate) {
					const startPos = dateToScrollPosition(startDate, interval);
					const endPos = dateToScrollPosition(endDate, interval);
					const lineWidth = Math.max(1, Math.abs(endPos - startPos)); // Ensure min width of 1px
					const line_left = (startPos - rounded_down_scroll_position) + visibleWidth / 2
					lineStyle = {
						left: `${line_left}px`,
						width: `${lineWidth}px`,
						height: `${lineHeight}px`,
						bottom: `${verticalPosition}px`,
						backgroundColor: line.color || '#000',
						opacity: line.opacity || 1,
						position: 'absolute',
						zIndex: 1
					};
				}
				else if (isStartVisible && !isEndVisible) {
					const startPos = dateToScrollPosition(startDate, interval);
					lineStyle = {
						left: `${(startPos - rounded_down_scroll_position) + visibleWidth / 2}px`,
						right: '0px',
						height: `${lineHeight}px`,
						bottom: `${verticalPosition}px`,
						backgroundColor: line.color || '#000',
						opacity: line.opacity || 1,
						position: 'absolute',
						zIndex: 1
					};
				}
				else if (!isStartVisible && isEndVisible && endDate) {
					const endPos = dateToScrollPosition(endDate, interval);
					lineStyle = {
						left: '0px',
						width: `${(endPos - rounded_down_scroll_position) + visibleWidth / 2}px`,
						height: `${lineHeight}px`,
						bottom: `${verticalPosition}px`,
						backgroundColor: line.color || '#000',
						opacity: line.opacity || 1,
						position: 'absolute',
						zIndex: 1
					};
				}
				else if (endDate) { // !isStartVisible && !isEndVisible - only if endDate exists
					const startPos = dateToScrollPosition(startDate, interval);
					const endPos = dateToScrollPosition(endDate, interval);
					const visibleAreaStart = scrollPosition - visibleWidth / 2;
					const visibleAreaEnd = scrollPosition + visibleWidth / 2;
					// Check if the line segment intersects the visible date range
					if (startPos <= visibleAreaEnd && endPos >= visibleAreaStart) {
						lineStyle = {
							left: '0px',
							right: '0px',
							height: `${lineHeight}px`,
							bottom: `${verticalPosition}px`,
							backgroundColor: line.color || '#000',
							opacity: line.opacity || 1,
							position: 'absolute',
							zIndex: 1
						};
					} else {
						return null; // Line is completely outside the visible range
					}
				}

				// Skip rendering if line or dot is outside vertical bounds after calculations
				if (verticalPosition > TIMELINE_HEIGHT + 50 || verticalPosition + lineHeight < -50) {
					return null;
				}

				return (
					<div key={`line-container-${event.id || event._id}-${lineIndex}`}>
						<div
							key={`line-${event.id || event._id}-${lineIndex}`}
							className="timeline-event-line"
							style={lineStyle}
						/>
					</div>
				);
			});
		}).flat().filter(Boolean); // Flatten arrays and remove nulls
	}, [events, scrollPosition, interval, visibleWidth, isDateVisible, scrollPositionY, TIMELINE_HEIGHT]);



	const renderAgeLines = useCallback(() => {
		if (!timelineData || !timelineData.ageLines) return null;
		return timelineData.ageLines.map((line, index) => {
			const verticalPosition = 5
			// Skip if way out of view
			if (verticalPosition < 0 || verticalPosition > TIMELINE_HEIGHT) return null;
			const lineHeight = 20;
			const startDate = line.startDate;
			const endDate = line.endDate;

			// Add null checks for age line dates
			if (!startDate || !endDate) return null; // Skip rendering if missing dates

			const isStartVisible = isDateVisible(scrollPositionToDate(dateToScrollPosition(startDate, interval), interval));
			const isEndVisible = isDateVisible(scrollPositionToDate(dateToScrollPosition(endDate, interval), interval));
			const rounded_down_scroll_position = (Math.floor(scrollPosition / interval.pixelWidth) * interval.pixelWidth)

			let lineStyle = {};

			if (isStartVisible && isEndVisible) {
				const startPos = dateToScrollPosition(startDate, interval);
				const endPos = dateToScrollPosition(endDate, interval);
				const lineWidth = Math.max(1, Math.abs(endPos - startPos));
				const line_left = (startPos - rounded_down_scroll_position) + visibleWidth / 2
				lineStyle = {
					left: `${line_left}px`,
					width: `${lineWidth}px`,
					height: `${lineHeight}px`,
					bottom: `${verticalPosition}px`,
					backgroundColor: line.color || '#000',
					opacity: 1,
					position: 'absolute',
					zIndex: 1,
					color: 'white',
					fontSize: '10px',
					lineHeight: `${lineHeight}px`, // Center text vertically
					textAlign: 'center',
					overflow: 'hidden',
					whiteSpace: 'nowrap',
				};
			}
			else if (isStartVisible && !isEndVisible) {
				const startPos = dateToScrollPosition(startDate, interval);
				lineStyle = {
					left: `${(startPos - rounded_down_scroll_position) + visibleWidth / 2}px`,
					right: '0px',
					height: `${lineHeight}px`,
					bottom: `${verticalPosition}px`,
					backgroundColor: line.color || '#000',
					opacity: 1,
					position: 'absolute',
					zIndex: 1,
					color: 'white',
					fontSize: '10px',
					lineHeight: `${lineHeight}px`,
					textAlign: 'center',
					overflow: 'hidden',
					whiteSpace: 'nowrap',
				};
			}
			else if (!isStartVisible && isEndVisible) {
				const endPos = dateToScrollPosition(endDate, interval);
				lineStyle = {
					left: '0px',
					width: `${(endPos - rounded_down_scroll_position) + visibleWidth / 2}px`,
					height: `${lineHeight}px`,
					bottom: `${verticalPosition}px`,
					backgroundColor: line.color || '#000',
					opacity: 1,
					position: 'absolute',
					zIndex: 1,
					color: 'white',
					fontSize: '10px',
					lineHeight: `${lineHeight}px`,
					textAlign: 'center',
					overflow: 'hidden',
					whiteSpace: 'nowrap',
				};

			}
			else { // !isStartVisible && !isEndVisible
				const startPos = dateToScrollPosition(startDate, interval);
				const endPos = dateToScrollPosition(endDate, interval);
				const visibleAreaStart = scrollPosition - visibleWidth / 2;
				const visibleAreaEnd = scrollPosition + visibleWidth / 2;
				if (startPos <= visibleAreaEnd && endPos >= visibleAreaStart) {
					lineStyle = {
						left: '0px',
						right: '0px',
						height: `${lineHeight}px`,
						bottom: `${verticalPosition}px`,
						backgroundColor: line.color || '#000',
						opacity: 1,
						position: 'absolute',
						zIndex: 1,
						color: 'white',
						fontSize: '10px',
						lineHeight: `${lineHeight}px`,
						textAlign: 'center',
						overflow: 'hidden',
						whiteSpace: 'nowrap',
					};
				} else {
					return null;
				}
			}

			return (
				<div
					key={`AgeLine-${line.id || index}`}
					className="timeline-event-line flex items-center justify-center"
					style={lineStyle}
				>
					{line.name}
				</div>
			);
		}).filter(Boolean); // Remove nulls
	}, [timelineData, scrollPosition, interval, visibleWidth, isDateVisible, scrollPositionY, TIMELINE_HEIGHT]);


	const renderShadedRegions = useCallback(() => {
		if (!timelineData || !timelineData.shadedRegions) return null;
		return timelineData.shadedRegions.map((region) => {
			const startDate = region.startDate;
			const endDate = region.endDate;

			// Add null checks for shaded region dates
			if (!startDate || !endDate) return null; // Skip rendering if missing dates

			const isStartVisible = isDateVisible(scrollPositionToDate(dateToScrollPosition(startDate, interval), interval));
			const isEndVisible = isDateVisible(scrollPositionToDate(dateToScrollPosition(endDate, interval), interval));
			const rounded_down_scroll_position = (Math.floor(scrollPosition / interval.pixelWidth) * interval.pixelWidth)

			let regionStyle = {};

			if (isStartVisible && isEndVisible) {
				const startPos = dateToScrollPosition(startDate, interval);
				const endPos = dateToScrollPosition(endDate, interval);
				const lineWidth = Math.max(1, Math.abs(endPos - startPos));
				const line_left = (startPos - rounded_down_scroll_position) + visibleWidth / 2
				regionStyle = {
					left: `${line_left}px`,
					width: `${lineWidth}px`,
					height: `${TIMELINE_HEIGHT}px`,
					bottom: `0px`,
					backgroundColor: region.color || '#000',
					opacity: region.opacity || 1,
					position: 'absolute',
					zIndex: 2,
				};
			}
			else if (isStartVisible && !isEndVisible) {
				const startPos = dateToScrollPosition(startDate, interval);
				regionStyle = {
					left: `${(startPos - rounded_down_scroll_position) + visibleWidth / 2}px`,
					right: '0px',
					height: `${TIMELINE_HEIGHT}px`,
					bottom: `0px`,
					backgroundColor: region.color || '#000',
					opacity: region.opacity || 1,
					position: 'absolute',
					zIndex: 2,
				};
			}
			else if (!isStartVisible && isEndVisible) {
				const endPos = dateToScrollPosition(endDate, interval);
				regionStyle = {
					left: '0px',
					width: `${(endPos - rounded_down_scroll_position) + visibleWidth / 2}px`,
					height: `${TIMELINE_HEIGHT}px`,
					bottom: `0px`,
					backgroundColor: region.color || '#000',
					opacity: region.opacity || 1,
					position: 'absolute',
					zIndex: 2,
				};

			}
			else { // !isStartVisible && !isEndVisible
				const startPos = dateToScrollPosition(startDate, interval);
				const endPos = dateToScrollPosition(endDate, interval);
				const visibleAreaStart = scrollPosition - visibleWidth / 2;
				const visibleAreaEnd = scrollPosition + visibleWidth / 2;
				if (startPos <= visibleAreaEnd && endPos >= visibleAreaStart) {
					regionStyle = {
						left: '0px',
						right: '0px',
						height: `${TIMELINE_HEIGHT}px`,
						bottom: `0px`,
						backgroundColor: region.color || '#000',
						opacity: region.opacity || 1,
						position: 'absolute',
						zIndex: 2,
					};
				} else {
					return null;
				}
			}

			return (
				<div
					key={`ShadedRegion-${region.id}`}
					className="timeline-event-line flex items-center justify-center"
					style={regionStyle}
				/>
			);
		}).filter(Boolean);
	}, [timelineData, scrollPosition, interval, visibleWidth, isDateVisible, TIMELINE_HEIGHT]);



	useEffect(() => {
		const handleResize = () => {
			// Adjust width considering scrollbar potentially taking space
			const scrollbarWidth = (maxeventVerticalHeight > TIMELINE_HEIGHT) ? 15 : 0; // Approx width + padding
			setVisibleWidth(window.innerWidth - scrollbarWidth);
		}
		handleResize(); // Initial call
		window.addEventListener('resize', handleResize);
		// Also call resize when maxeventVerticalHeight changes, as it affects scrollbar visibility
		return () => window.removeEventListener('resize', handleResize);
	}, [maxeventVerticalHeight, TIMELINE_HEIGHT]) // Add dependencies


	useEffect(() => {
		window.timelineEvents = events;
		window.selectEvent = onEventSelect;
		const handleTimelineEventSelect = (e) => {
			console.log("Timeline event seçildi:", e.detail ? e.detail.title : "Bilinmeyen");
			if (e.detail && onEventSelect) onEventSelect(e.detail);
		}
		document.addEventListener('selectTimelineEvent', handleTimelineEventSelect);
		return () => document.removeEventListener('selectTimelineEvent', handleTimelineEventSelect);
	}, [events, onEventSelect]);

	useEffect(() => {
		const newDate = scrollPositionToDate(scrollPosition, interval);
		if (prevDateRef.current && prevDateRef.current.toString() !== newDate.toString() && prevIntervalRef.current === interval) {
			onDateChange(newDate);
		}
		prevDateRef.current = newDate;
		prevIntervalRef.current = interval;
	}, [scrollPosition, interval, onDateChange]);

	const handleMouseDown = useCallback((e) => {
		// Prevent starting drag if clicking on the scrollbar itself
		if (e.target.closest('.vertical-scrollbar-thumb') || e.target.closest('.vertical-scrollbar-track')) {
			return;
		}
		setIsDragging(true);
		setStartX(e.pageX);
		setStartY(e.pageY);
		// Explicitly set cursor style on body to prevent text selection cursor flickers
		document.body.style.cursor = 'grabbing';
	}, [])

	// Calculate minimum scroll Y based on content height and timeline height
	const minScrollY = useMemo(() => {
		return maxeventVerticalHeight > TIMELINE_HEIGHT
			? -(maxeventVerticalHeight - TIMELINE_HEIGHT)
			: 0;
	}, [maxeventVerticalHeight, TIMELINE_HEIGHT]);


	const handleMouseMove = useCallback((e) => {
		if (!isDragging) return;
		const deltaX = e.pageX - startX;
		const deltaY = e.pageY - startY;
		setScrollPosition((prev) => prev - deltaX);
		if (scrollPosition === scrollPosition)   // NaN kontrolü
			sessionStorage.setItem('timelineScrollPosition', scrollPosition)

		setScrollPositionY((prev) => {
			const newValue = prev - deltaY;
			// Clamp the scroll position between 0 (top) and minScrollY (bottom)
			return Math.max(Math.min(newValue, 0), minScrollY);
		});

		setStartX(e.pageX);
		setStartY(e.pageY);
	}, [isDragging, startX, startY, setScrollPosition, setScrollPositionY, minScrollY]); // Added minScrollY dependency

	const handleMouseUp = useCallback(() => {
		if (isDragging) {
			setIsDragging(false);
			document.body.style.cursor = 'default'; // Reset body cursor
		}
	}, [isDragging])

	// --- Touch Handlers (mobile swipe) - useRef ile state async sorununu önle ---
	const touchStartRef = useRef(null);

	// Touch handler'ları useEffect içinde native olarak bağlanıyor (aşağıda)

	// --- Scrollbar Drag Handlers ---
	const handleScrollbarMouseDown = useCallback((e) => {
		setIsScrollbarDragging(true);
		setScrollbarStartY(e.clientY); // Use clientY for vertical position
		e.stopPropagation(); // Prevent main area drag
		document.body.style.cursor = 'grabbing'; // Use grabbing cursor
	}, []);

	const handleScrollbarMouseMove = useCallback((e) => {
		if (!isScrollbarDragging) return;

		const deltaY = e.clientY - scrollbarStartY;
		setScrollbarStartY(e.clientY);

		// Calculate scrollbar dimensions
		const trackHeight = TIMELINE_HEIGHT;
		const thumbHeightRatio = TIMELINE_HEIGHT / maxeventVerticalHeight;
		const thumbHeight = Math.max(20, thumbHeightRatio * trackHeight); // Min height 20px
		const scrollableTrackHeight = trackHeight - thumbHeight;

		if (scrollableTrackHeight <= 0) return; // Avoid division by zero

		// Calculate how much the content should scroll
		const totalContentScrollRange = maxeventVerticalHeight - TIMELINE_HEIGHT;
		const contentDeltaY = (deltaY / scrollableTrackHeight) * totalContentScrollRange;

		setScrollPositionY((prev) => {
			const newValue = prev + contentDeltaY; // Subtract because thumb moving down means content scrolls up (negative scrollY)
			// Clamp the scroll position
			return Math.max(Math.min(newValue, 0), minScrollY);
		});

	}, [isScrollbarDragging, scrollbarStartY, setScrollPositionY, maxeventVerticalHeight, TIMELINE_HEIGHT, minScrollY]);

	const handleScrollbarMouseUp = useCallback(() => {
		if (isScrollbarDragging) {
			setIsScrollbarDragging(false);
			document.body.style.cursor = 'default'; // Reset cursor
		}
	}, [isScrollbarDragging]);
	// --- End Scrollbar Drag Handlers ---


	useEffect(() => {
		// Add/remove listeners based on which drag is active
		const handleGlobalMouseMove = (e) => {
			if (isDragging) handleMouseMove(e);
			if (isScrollbarDragging) handleScrollbarMouseMove(e);
		};
		const handleGlobalMouseUp = (e) => {
			if (isDragging) handleMouseUp(e);
			if (isScrollbarDragging) handleScrollbarMouseUp(e);
		};

		if (isDragging || isScrollbarDragging) {
			document.addEventListener("mouseup", handleGlobalMouseUp);
			document.addEventListener("mousemove", handleGlobalMouseMove);
		}

		return () => {
			document.removeEventListener("mouseup", handleGlobalMouseUp);
			document.removeEventListener("mousemove", handleGlobalMouseMove);
			// Ensure cursor is reset if component unmounts while dragging
			if (isDragging || isScrollbarDragging) {
				document.body.style.cursor = 'default';
			}
		}
	}, [isDragging, isScrollbarDragging, handleMouseMove, handleMouseUp, handleScrollbarMouseMove, handleScrollbarMouseUp]);

	// Native touch listener - passive:false ile preventDefault çalışsın (sayfa scroll'u engellensin)
	// Tüm touch mantığı burada ref ile yönetiliyor (state async sorununu önlemek için)
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const onTouchStart = (e) => {
			if (e.touches.length !== 1) return;
			touchStartRef.current = {
				x: e.touches[0].pageX,
				y: e.touches[0].pageY,
			};
		};

		const onTouchMove = (e) => {
			if (!touchStartRef.current || e.touches.length !== 1) return;
			e.preventDefault();
			const deltaX = e.touches[0].pageX - touchStartRef.current.x;
			const deltaY = e.touches[0].pageY - touchStartRef.current.y;
			touchStartRef.current = {
				x: e.touches[0].pageX,
				y: e.touches[0].pageY,
			};
			setScrollPosition((prev) => prev - deltaX);
			setScrollPositionY((prev) => {
				const newValue = prev - deltaY;
				return Math.max(Math.min(newValue, 0), minScrollY);
			});
		};

		const onTouchEnd = () => {
			touchStartRef.current = null;
		};

		el.addEventListener('touchstart', onTouchStart, { passive: true });
		el.addEventListener('touchmove', onTouchMove, { passive: false });
		el.addEventListener('touchend', onTouchEnd, { passive: true });

		return () => {
			el.removeEventListener('touchstart', onTouchStart);
			el.removeEventListener('touchmove', onTouchMove);
			el.removeEventListener('touchend', onTouchEnd);
		};
	}, [minScrollY, setScrollPosition, setScrollPositionY]);


	const getTimelineStyle = useCallback(() => {
		const baseStyle = {
			// Adjust width calculation if needed, handled by resize effect
			width: `calc(100% - ${maxeventVerticalHeight > TIMELINE_HEIGHT ? '15px' : '0px'})`,
			height: `${TIMELINE_HEIGHT}px`,
			border: "2px solid #ccc",
			borderRadius: "8px",
			// Use default cursor unless dragging canvas, scrollbar handles its own cursor logic
			cursor: isDragging ? 'grabbing' : 'grab',
			userSelect: "none",
			position: "relative",
			overflow: "hidden", // Important: clips content outside bounds
		}

		if (timelineBackground?.type === "image") {
			return {
				...baseStyle,
				backgroundImage: `url(${timelineBackground.value})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}
		}
		return {
			...baseStyle,
			backgroundColor: timelineBackground?.value || "#f0f0f0",
		}
	}, [visibleWidth, TIMELINE_HEIGHT, isDragging, timelineBackground, maxeventVerticalHeight]); // Added maxeventVerticalHeight

	// --- Scrollbar Calculations ---
	const showScrollbar = maxeventVerticalHeight > TIMELINE_HEIGHT;
	const scrollbarThumbHeight = showScrollbar
		? Math.max(20, (TIMELINE_HEIGHT / maxeventVerticalHeight) * TIMELINE_HEIGHT)
		: 0;
	const scrollableTrackHeight = TIMELINE_HEIGHT - scrollbarThumbHeight;
	// Calculate thumb top position based on scrollPositionY
	// Map scrollPositionY (range: minScrollY to 0) to thumb top (range: scrollableTrackHeight to 0)
	const scrollbarThumbTop = showScrollbar && minScrollY < 0
		? (scrollPositionY / minScrollY) * scrollableTrackHeight
		: 0;
	// --- End Scrollbar Calculations ---

	// Add listener for filter updates
	useEffect(() => {
		const handleFilterUpdate = () => {
			// Force redraw when filters are updated
			console.log("ScrollCounter detected filter update");

			// Trigger a small rerender by forcing a small scroll position change and back
			const currentPos = scrollPosition;
			setScrollPosition(prev => prev + 0.01);
			setTimeout(() => {
				setScrollPosition(currentPos);
			}, 50);
		};

		document.addEventListener('timelineFilterUpdated', handleFilterUpdate);
		return () => {
			document.removeEventListener('timelineFilterUpdated', handleFilterUpdate);
		};
	}, [scrollPosition]);

	return (
		<div className="flex flex-col items-center justify-center w-full"> {/* Ensure full width, no padding */}
			<div className="relative w-full flex" style={{ height: `${TIMELINE_HEIGHT}px` }}> {/* Wrapper for timeline + scrollbar */}
				<div
					ref={containerRef}
					onMouseDown={handleMouseDown}
					style={getTimelineStyle()}
				>
					{/* Filter Indicator */}
					{isFiltered && (
						<div
							style={{
								position: 'absolute',
								top: '10px',
								right: '10px', // Adjust position if scrollbar is present? Maybe keep it relative to content area
								padding: '4px 8px',
								backgroundColor: 'rgba(59, 130, 246, 0.8)',
								color: 'white',
								borderRadius: '9999px',
								fontSize: '11px',
								fontWeight: 'bold',
								zIndex: 10,
								boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
							</svg>
							Filtrelenmiş Görünüm
						</div>
					)}
					{useMemo(() => renderAgeLines(), [renderAgeLines])}
					{useMemo(() => renderEventLines(), [renderEventLines])}
					{useMemo(() => renderShadedRegions(), [renderShadedRegions])}

					{/* Interval Timeline */}
					<IntervalTimeline
						scrollPosition={scrollPosition}
						interval={interval}
						visibleWidth={visibleWidth} // Pass the calculated visible width
						events={events}
						categories={categories}
						stylings={stylings}
						onMarkerCountUpdate={handleMarkerCountUpdate}
						style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10 }}
						scrollPositionY={scrollPositionY} // Pass scrollY to potentially adjust rendering
					/>
				</div>

				{/* Vertical Scrollbar */}
				{/* TODO Vertical Scrollbar DISABLED*/}
				{false && showScrollbar && (
					<div
						className="vertical-scrollbar-track"
						style={{
							position: 'absolute',
							right: 0,
							top: 0,
							width: '10px', // Width of the track
							height: `${TIMELINE_HEIGHT}px`,
							backgroundColor: 'rgba(200, 200, 200, 0.5)', // Semi-transparent grey
							borderRadius: '5px',
							zIndex: 20,
						}}
					>
						<div
							className="vertical-scrollbar-thumb"
							onMouseDown={handleScrollbarMouseDown} // Attach mouse down handler
							style={{
								position: 'absolute',
								right: '1px', // Center it slightly within the track
								bottom: `${scrollbarThumbTop}px`, // Dynamic top position
								width: '8px', // Slightly narrower than track
								height: `${scrollbarThumbHeight}px`, // Dynamic height
								backgroundColor: 'rgba(100, 100, 100, 0.8)', // Darker grey
								borderRadius: '4px',
								cursor: 'grab', // Indicate draggable
							}}
						/>
					</div>
				)}
			</div>

			{/* Event Popup */}
			{selectedEvent && (
				<EventPopup
					event={selectedEvent}
					onClose={() => {
						onEventSelect(null);
						window.selectedEvent = null;
					}}
					timelineData={{
						...timelineData,
						events: events // Pass current events, potentially filtered
					}}
					onTimelineUpdate={onTimelineUpdate}
					isAuthenticated={isAuthenticated}
					categories={categories}
					onCategoryFilter={onCategoryFilter}
				/>
			)}
		</div>
	)
}

export default memo(ScrollCounter)
